import type { DashboardLayout } from '@promanage/core';

/**
 * Where each overview lives, and when a visit to `/` should go to another one.
 *
 * THE CHOICE ITSELF IS ON THE USER — `users.dashboard_layout`, read off `auth.me` — so it follows
 * somebody from the office machine to their laptop. Only the top bar's menu changes it; opening a
 * colleague's link to any of the three shows that page and leaves the choice alone.
 */
export const DASHBOARD_LAYOUT_PATHS = {
  finance: '/',
  management: '/dashboard/management',
  custom: '/dashboard/custom',
} as const satisfies Record<DashboardLayout, string>;

/**
 * Whether a visit to `/` should open a different overview, and which.
 *
 * `/` IS THE FINANCE OVERVIEW'S ADDRESS, so it is the one that has to decide. Null means stay.
 *
 *   - SOMEBODY WHO MAY NOT OPEN FINANCE NEVER STAYS — a period in the link included, which is
 *     carried across. The server refuses them `dashboard.summary` regardless
 *     (`canViewFinanceDashboard`); this is only so they land on a page that works. They go to their
 *     custom page if that is their choice, and to property management otherwise.
 *   - OTHERWISE, A BARE `/` — the rail, the logo, the landing after sign-in — is "my dashboard", and
 *     opens on the chosen layout. A `/` CARRYING A PERIOD is a link to these figures and is
 *     honoured as it stands: "look at August" must not be redirected to a page nobody asked for.
 */
export function dashboardRedirect(visit: {
  searchStr: string;
  layout: DashboardLayout | undefined;
  mayOpenFinance: boolean;
}): (typeof DASHBOARD_LAYOUT_PATHS)['management' | 'custom'] | null {
  if (!visit.mayOpenFinance) {
    return visit.layout === 'custom' ? DASHBOARD_LAYOUT_PATHS.custom : DASHBOARD_LAYOUT_PATHS.management;
  }
  if (visit.searchStr !== '') return null;
  if (visit.layout === 'management') return DASHBOARD_LAYOUT_PATHS.management;
  if (visit.layout === 'custom') return DASHBOARD_LAYOUT_PATHS.custom;
  return null;
}
